import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { platform } from '../platform';
import { loadIdentity, signIn as doSignIn, signOut as doSignOut, onSessionLost, onAppAccessLost, onAccessEnded, refreshProfile } from '../session/session';
import { ensureStore, shutdown, onSyncStatus, getSync, wipeLocal } from '../session/boot';
import { clearPersonCache } from '../data/usePerson';

const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

// Gate for the whole app: 'loading' → 'signedOut' | 'ready' ('expired' / 'noAccess' = back to the login screen, data kept). 'ready' never needs the network —
// the identity and the data are both on the device.
export function SessionProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [identity, setIdentity] = useState(null);
  const [sync, setSync] = useState({ state: 'idle', pending: 0, failed: 0, lastSyncAt: null, lastError: null });
  const [online, setOnline] = useState(true);
  const [ended, setEnded] = useState(false);   // people.md P5: this person's access to the team ended — the store was wiped
  const wasOnline = useRef(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const id = await loadIdentity();
      if (!alive) return;
      if (!id) { setStatus('signedOut'); return; }
      await ensureStore(id);
      if (!alive) return;
      setIdentity(id); setStatus('ready');
    })().catch((e) => { console.error('boot failed', e); if (alive) setStatus('signedOut'); });
    return () => { alive = false; };
  }, []);

  useEffect(() => onSyncStatus((st) => { setSync(st); platform().background.followOutbox(st); }), []);

  useEffect(() => {
    const p = platform();
    const unsubNet = p.net.subscribe((up) => {
      setOnline(up);
      getSync()?.setOnline(up);   // offline → the engine sleeps; back online → one push+pull
      wasOnline.current = up;
    });
    // Leaving the screen pushes anything waiting once, then the engine goes quiet. If it's
    // still waiting (no signal), the WorkManager job takes over — see mobile services/syncTask.js.
    const unsubApp = p.appState.subscribe((st) => {
      const s = getSync();
      if (!s) return;
      if (st === 'active') { s.setForeground(true); return; }
      if (st !== 'background') return;
      s.setForeground(false);
      if (s.status().pending > 0) p.background.armSyncTask();
    });
    return () => { unsubNet(); unsubApp(); };
  }, []);

  const signOut = useCallback(async () => {
    await shutdown();
    await doSignOut();
    clearPersonCache();
    setIdentity(null); setStatus('signedOut');
  }, []);

  // Their employment ended (or the account was switched off): nothing of the org stays on the device — the local store
  // is wiped, pending changes included, and the login screen says why. Signing in again is refused by the server.
  const identityNow = useRef(identity);
  identityNow.current = identity;
  useEffect(() => onAccessEnded(async () => {
    const id = identityNow.current || (await loadIdentity());
    if (!id) return;
    await wipeLocal(id).catch(() => {});
    await doSignOut().catch(() => {});
    clearPersonCache();
    setEnded(true);
    setIdentity(null); setStatus('noAccess');
  }), []);

  // Server revoked the session (password change, disabled). Data stays on the device;
  // signing back in as the same person picks up where it left off.
  useEffect(() => onSessionLost(() => { setStatus((s) => (s === 'ready' ? 'expired' : s)); }), []);

  // An admin took BattleOps off this account. Same as an ended session — the data stays on the
  // device, the login screen says why, and signing in works again once access is back.
  useEffect(() => onAppAccessLost(() => { setStatus((s) => (s === 'ready' ? 'noAccess' : s)); }), []);

  const signIn = useCallback(async (email, password) => {
    const id = await doSignIn(email, password);
    setEnded(false);
    await ensureStore(id);
    setIdentity(id); setStatus('ready');
    getSync()?.syncNow();
  }, []);

  const syncNow = useCallback(() => getSync()?.syncNow(), []);

  // Role and grants can change on the server (an admin edits them): re-read once signed in and
  // each time the app comes back, when there's signal. Offline it keeps the last known.
  const identityRef = useRef(identity);
  identityRef.current = identity;
  useEffect(() => {
    if (status !== 'ready') return;
    const refresh = () => {
      const cur = identityRef.current;
      if (!cur) return;
      refreshProfile(cur).then((next) => { if (next !== cur) setIdentity(next); }).catch(() => {});
    };
    refresh();
    return platform().appState.subscribe((st) => { if (st === 'active') refresh(); });
  }, [status]);

  return (
    <SessionContext.Provider value={{ status, identity, access: identity?.access ?? null, sync, online, signIn, signOut, syncNow, ended }}>
      {children}
    </SessionContext.Provider>
  );
}
